import {
  ANNUAL_RETENTION_DISCOUNT_PERCENTAGE_OFF,
  JUNGLE_CLOUDFLARE_BASE_URL,
  MONTHLY_RETENTION_DISCOUNT_PERCENTAGE_OFF,
  PriceDetails,
} from "../../constants/GlobalTypesAndEnumsAndConstants";

// Stripe prices and their formatting, with nothing of the app's runtime attached: the marketing site's
// pricing page (jungleai.com/pricing) shows the same prices as the paywall and must load without the
// app's stores. monetizationHelperMethods.ts adds the in-app caching on top of this module.

export const CURRENCY_MAP = {
  US: {
    symbol: "$",
    annualSuperLearnerPrice: "72",
    monthlySuperLearnerPrice: "15",
    currency: "USD",
  },
};

// Mapping of currencies to the desired number of decimal places
const currencyFractionDigits = {
  // Currencies with no decimal places
  JPY: 0,
  CLP: 0,
  VND: 0,
  COP: 0,
  KRW: 0,
  USD: 0,
  IDR: 0,
  // Currencies with two decimal places
  EUR: 2,
  GBP: 2,
  CAD: 2,
  AUD: 2,
  NZD: 2,
  CHF: 2,
  SEK: 2,
  NOK: 2,
  DKK: 2,
  PLN: 2,
  CZK: 2,
  HUF: 2,
  RON: 2,
  BGN: 2,
  HRK: 2,
  RUB: 2,
  TRY: 2,
  BRL: 2,
  MXN: 2,
  ARS: 2,
  PEN: 2,
  UYU: 2,
  ZAR: 2,
  INR: 2,
  PHP: 2,
  SGD: 2,
  MYR: 2,
  THB: 2,
  CNY: 2,
  HKD: 2,
  TWD: 2,
  AED: 2,
  SAR: 2,
  EGP: 2,
  MAD: 2,
  PKR: 2,
  // Add other currencies as needed
};

type CurrencyMapDetail = {
  symbol: string;
  annualSuperLearnerPrice: string;
  monthlySuperLearnerPrice: string;
  currency: string;
};

//

// ==========================
//  Monetization Helper Methods
// ==========================
// All helper functions related to pricing, currency formatting and subscription plans live here to avoid bloating other utility files.
//

/**
 * Build a mapping from currency code to a suitable locale. This is used by Intl.NumberFormat.
 */
const buildCurrencyToLocaleMap = () => {
  // Comprehensive currency to locale mapping to ensure proper decimal separators
  const currencyToLocaleMap: Record<string, string> = {
    // Currencies typically using comma as decimal separator
    EUR: "de-DE", // Euro (Germany as representative)
    PLN: "pl-PL", // Polish Złoty
    CZK: "cs-CZ", // Czech Koruna
    HUF: "hu-HU", // Hungarian Forint
    RON: "ro-RO", // Romanian Leu
    BGN: "bg-BG", // Bulgarian Lev
    HRK: "hr-HR", // Croatian Kuna
    NOK: "nb-NO", // Norwegian Krone
    SEK: "sv-SE", // Swedish Krona
    DKK: "da-DK", // Danish Krone
    TRY: "tr-TR", // Turkish Lira
    BRL: "pt-BR", // Brazilian Real
    ARS: "es-AR", // Argentine Peso
    CLP: "es-CL", // Chilean Peso
    COP: "es-CO", // Colombian Peso
    PEN: "es-PE", // Peruvian Sol
    UYU: "es-UY", // Uruguayan Peso

    // Currencies typically using period as decimal separator
    USD: "en-US", // US Dollar
    GBP: "en-GB", // British Pound
    CAD: "en-CA", // Canadian Dollar
    AUD: "en-AU", // Australian Dollar
    NZD: "en-NZ", // New Zealand Dollar
    CHF: "de-CH", // Swiss Franc (Switzerland uses period)
    MXN: "es-MX", // Mexican Peso (Mexico uses period)
    INR: "en-IN", // Indian Rupee
    PKR: "ur-PK", // Pakistani Rupee
    ZAR: "en-ZA", // South African Rand
    AED: "ar-AE", // UAE Dirham
    SAR: "ar-SA", // Saudi Riyal
    EGP: "ar-EG", // Egyptian Pound
    MAD: "ar-MA", // Moroccan Dirham
    PHP: "en-PH", // Philippine Peso
    SGD: "en-SG", // Singapore Dollar
    MYR: "ms-MY", // Malaysian Ringgit
    THB: "th-TH", // Thai Baht
    IDR: "id-ID", // Indonesian Rupiah
    CNY: "zh-CN", // Chinese Yuan
    HKD: "zh-HK", // Hong Kong Dollar
    TWD: "zh-TW", // Taiwan Dollar
    JPY: "ja-JP", // Japanese Yen
    KRW: "ko-KR", // South Korean Won
    VND: "vi-VN", // Vietnamese Dong
    RUB: "ru-RU", // Russian Ruble
  };

  // Add any currencies from CURRENCY_MAP that might not be in our comprehensive list
  for (const countryCode in CURRENCY_MAP) {
    const currencyInfo = CURRENCY_MAP[countryCode as keyof typeof CURRENCY_MAP];
    const currencyCode = currencyInfo.currency;

    // Only add if not already mapped
    if (!currencyToLocaleMap[currencyCode]) {
      const languageCode = getLanguageCodeForCountry(countryCode);
      if (languageCode) {
        currencyToLocaleMap[currencyCode] = `${languageCode}-${countryCode}`;
      }
    }
  }

  return currencyToLocaleMap;
};

/**
 * Map a country code to a representative language code for building the locale – only the subset needed for pricing.
 */
const getLanguageCodeForCountry = (countryCode: string) => {
  const countryToLanguageMap: Record<string, string> = {
    US: "en",
    CA: "en",
    MX: "es",
    GB: "en",
    EU: "en",
    IN: "en",
    PH: "en",
    PK: "ur",
    MY: "ms",
    ID: "id",
    AE: "ar",
    KR: "ko",
    AR: "es",
    BR: "pt",
    CO: "es",
    CL: "es",
    PE: "es",
    AU: "en",
    ZA: "en",
    EG: "ar",
    MA: "ar",
  };
  return countryToLanguageMap[countryCode];
};

/** Format a numeric price based on currency */
function formatPrice(price: number, currencyCode: string) {
  const currencyToLocaleMap = buildCurrencyToLocaleMap();
  // Default to en-US if no locale is found for the currency
  const locale = currencyToLocaleMap[currencyCode] || "en-US";

  const maximumFractionDigits = currencyFractionDigits[currencyCode as keyof typeof currencyFractionDigits] ?? 0;

  const decimalPlacesToRound = maximumFractionDigits >= 1 ? 1 : 0;
  const factor = Math.pow(10, decimalPlacesToRound);
  const roundedPrice = Math.ceil(price * factor) / factor;

  return new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits,
    useGrouping: true,
  }).format(roundedPrice);
}

/**
 * Format a Stripe MINOR-UNIT amount (the raw integer Stripe returns, e.g. an
 * invoice's amount_due) into a localized MAJOR-unit number string.
 *
 * `formatPrice` ceils to whole units for clean list-price display ($30, $180) and
 * treats USD as 0-decimal — so it renders a $17.87 charge as "$18". Prorated /
 * charged amounts must show the true figure. Crucially, the minor→major
 * conversion is currency-aware: ÷100 for 2-decimal currencies (USD/EUR/GBP) but
 * ÷1 for zero-decimal currencies (JPY/KRW/VND) — hardcoding ÷100 would render a
 * ¥5000 charge as "¥50". Stripe returns lowercase codes ("usd"/"jpy"), so we
 * normalize to upper for every lookup. Callers prepend the currency symbol,
 * matching the formatPrice call sites.
 */
function formatCurrencyMinorUnits(minorUnitAmount: number, currencyCode: string): string {
  const normalizedCurrencyCode = (currencyCode || "USD").toUpperCase();
  let fractionDigits = 2;
  try {
    fractionDigits = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: normalizedCurrencyCode,
    }).resolvedOptions().maximumFractionDigits;
  } catch {
    fractionDigits = 2;
  }
  const majorUnitAmount = minorUnitAmount / Math.pow(10, fractionDigits);
  const locale = buildCurrencyToLocaleMap()[normalizedCurrencyCode] || "en-US";
  return new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping: true,
  }).format(majorUnitAmount);
}

/** Return pricing details for a given country code */
const getStripePriceDetailsForCountryCode = (countryCode: string): CurrencyMapDetail => {
  // Always return USD prices as fallback since we're using Stripe API for actual prices
  return CURRENCY_MAP["US"];
};

/** Fetch prices from Stripe API */
async function fetchStripePrices(countryCode: string): Promise<PriceDetails | null> {
  try {
    const response = await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/payments/stripe_prices`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        country_code: countryCode,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch Stripe prices: ${response.status}`);
    }

    const data = await response.json();

    if (!data.currency || !data.symbol) {
      throw new Error("Invalid response format from Stripe prices API");
    }

    // == null catches both null and undefined — the worker may send monthlySuperLearnerPrice: null
    // for legacy weekly-plan countries, which would otherwise pass through as String(null) → "NaN".
    if (data.monthlySuperLearnerPrice == null || data.annualSuperLearnerPrice == null) {
      throw new Error("Invalid response format from Stripe prices API - missing monthly prices");
    }
    // Elite prices are optional in the worker response — legacy responses will not
    // include them and that's fine; we fall back to null in getStripePriceDetails.
    return {
      currency: data.currency,
      symbol: data.symbol,
      monthlySuperLearnerPrice: String(data.monthlySuperLearnerPrice),
      annualSuperLearnerPrice: String(data.annualSuperLearnerPrice),
      monthlyElitePrice: data.monthlyElitePrice != null ? String(data.monthlyElitePrice) : undefined,
      annualElitePrice: data.annualElitePrice != null ? String(data.annualElitePrice) : undefined,
      // Optional like Elite's: an older Worker sends none, and the exam pass is then not offered
      examPassPrices:
        data.examPassPrices && typeof data.examPassPrices === "object"
          ? {
              short: data.examPassPrices.short != null ? String(data.examPassPrices.short) : null,
              two_weeks: data.examPassPrices.two_weeks != null ? String(data.examPassPrices.two_weeks) : null,
              month: data.examPassPrices.month != null ? String(data.examPassPrices.month) : null,
            }
          : undefined,
    };
  } catch (error) {
    console.error("Error fetching Stripe prices:", error);
    return null;
  }
}

/** Internal helper that loads country-aware Stripe prices with a USD fallback. */
async function initializePrices(countryCode: string): Promise<PriceDetails> {
  // First, try to fetch prices from Stripe API
  const stripePrices = await fetchStripePrices(countryCode);
  if (stripePrices) {
    return stripePrices;
  }

  // If Stripe API fails, fall back to USD prices only
  const usdPrices = CURRENCY_MAP["US"];
  return usdPrices;
}

/** Public API: fetch and cache price details, plus some convenience formatted prices */

/** The price details the paywall and the pricing page display, derived from a country's prices. */
export function formatStripePriceDetails(priceDetails: PriceDetails) {
  const formattedSuperLearnerMonthlyPrice = formatPrice(
    Number(priceDetails?.monthlySuperLearnerPrice || 0),
    priceDetails?.currency?.toString()
  );
  const formattedSuperLearnerPriceOfMonthlyIfPaidForFullYear = formatPrice(
    Number(priceDetails?.monthlySuperLearnerPrice || 0) * 12,
    priceDetails?.currency?.toString()
  );
  const formattedSuperLearnerAnnualPriceProratedMonthly = formatPrice(
    Number(priceDetails?.annualSuperLearnerPrice || 0) / 12,
    priceDetails?.currency?.toString()
  );

  const formattedSuperLearnerFullAnnualPrice = formatPrice(
    Number(priceDetails?.annualSuperLearnerPrice || 0),
    priceDetails?.currency?.toString()
  );

  const superLearnerMonthlyDiscountPrice = priceDetails?.monthlySuperLearnerPrice
    ? formatPrice(
        Number(priceDetails.monthlySuperLearnerPrice) * (1 - MONTHLY_RETENTION_DISCOUNT_PERCENTAGE_OFF / 100),
        priceDetails?.currency?.toString()
      )
    : null;

  const superLearnerAnnualDiscountPrice = formatPrice(
    Number(priceDetails?.annualSuperLearnerPrice || 0) * (1 - ANNUAL_RETENTION_DISCOUNT_PERCENTAGE_OFF / 100),
    priceDetails?.currency?.toString()
  );

  // NEW — Elite prices extracted from the worker payload (optional; null when worker
  // doesn't yet return Elite prices or when the Stripe API call falls back to USD defaults).
  const monthlyElitePriceFromWorker = priceDetails?.monthlyElitePrice;
  const annualElitePriceFromWorker = priceDetails?.annualElitePrice;

  const formattedEliteMonthlyPrice = monthlyElitePriceFromWorker
    ? formatPrice(Number(monthlyElitePriceFromWorker), priceDetails?.currency?.toString())
    : null;
  const formattedEliteFullAnnualPrice = annualElitePriceFromWorker
    ? formatPrice(Number(annualElitePriceFromWorker), priceDetails?.currency?.toString())
    : null;
  const formattedEliteAnnualPriceProratedMonthly = annualElitePriceFromWorker
    ? formatPrice(Number(annualElitePriceFromWorker) / 12, priceDetails?.currency?.toString())
    : null;
  const formattedElitePriceOfMonthlyIfPaidForFullYear = monthlyElitePriceFromWorker
    ? formatPrice(Number(monthlyElitePriceFromWorker) * 12, priceDetails?.currency?.toString())
    : null;

  const eliteMonthlyDiscountPrice = monthlyElitePriceFromWorker
    ? formatPrice(
        Number(monthlyElitePriceFromWorker) * (1 - MONTHLY_RETENTION_DISCOUNT_PERCENTAGE_OFF / 100),
        priceDetails?.currency?.toString()
      )
    : null;
  const eliteAnnualDiscountPrice = annualElitePriceFromWorker
    ? formatPrice(
        Number(annualElitePriceFromWorker) * (1 - ANNUAL_RETENTION_DISCOUNT_PERCENTAGE_OFF / 100),
        priceDetails?.currency?.toString()
      )
    : null;

  return {
    formattedSuperLearnerMonthlyPrice,
    formattedSuperLearnerFullAnnualPrice,
    formattedSuperLearnerAnnualPriceProratedMonthly,
    formattedSuperLearnerPriceOfMonthlyIfPaidForFullYear,
    superLearnerMonthlyDiscountPrice,
    superLearnerAnnualDiscountPrice,
    // NEW — Elite fields (null when worker doesn't yet return Elite pricing)
    formattedEliteMonthlyPrice,
    formattedEliteFullAnnualPrice,
    formattedEliteAnnualPriceProratedMonthly,
    formattedElitePriceOfMonthlyIfPaidForFullYear,
    eliteMonthlyDiscountPrice,
    eliteAnnualDiscountPrice,
    eliteMonthlyProductId: null, // Stripe path has no product IDs; populated for Apple/Play paths
    eliteAnnualProductId: null,
    priceDetails,
  };
}

export type StripePriceDetails = ReturnType<typeof formatStripePriceDetails>;

export {
  initializePrices,
  getStripePriceDetailsForCountryCode,
  formatPrice,
  formatCurrencyMinorUnits,
  buildCurrencyToLocaleMap,
  fetchStripePrices,
};
